<script setup lang="ts">
import { ref, onMounted, computed, onBeforeUnmount, watch, defineProps } from "vue";
import moment from 'moment';
import { displayService } from '@/_services';

const props = defineProps<{
  timeFormat: string;
  showSeconds: boolean;
  fontFamily: string;
  fontSize: number;
  fontColor: string;
  fontWeight: string;
  template: string;
  backgroundColor: string;
  seperatorColor: string;
  showSeperator: boolean;
  config: any;
  timeZone: string;
}>();

const scaledFontSize = computed(() => {
  if (!props.fontSize) return 40;
  return Math.round(props.fontSize);
});

const currentTime = ref(new Date());
const clockTimer = ref<number | null>(null);
const timezoneOffset = ref<number | null>(null); // Store the timezone offset in milliseconds

const previousTime = ref({
  hours: [0, 0],
  minutes: [0, 0],
  seconds: [0, 0],
});

const formattedTime = computed(() => {
  const hours = currentTime.value.getHours();
  const minutes = currentTime.value.getMinutes();
  const seconds = currentTime.value.getSeconds();

  let formattedHours = hours;
  let hourString = "";

  if (props.timeFormat === "12h") {
    formattedHours = hours % 12;
    formattedHours = formattedHours || 12;
    hourString = formattedHours.toString();
  } else {
    hourString = formattedHours.toString().padStart(2, "0");
  }

  const formattedMinutes = minutes.toString().padStart(2, "0");
  let timeString = `${hourString}:${formattedMinutes}`;

  if (props.showSeconds) {
    const formattedSeconds = seconds.toString().padStart(2, "0");
    timeString += `:${formattedSeconds}`;
  }

  if (props.timeFormat === "12h") {
    timeString += hours >= 12 ? " PM" : " AM";
  }

  return timeString;
});

const digitalTime = computed(() => {
  const hours = currentTime.value.getHours();
  const minutes = currentTime.value.getMinutes();
  const seconds = currentTime.value.getSeconds();

  let formattedHours = hours;
  if (props.timeFormat === "12h") {
    formattedHours = hours % 12;
    formattedHours = formattedHours || 12; // 0 as 12
  }

  const hourString = formattedHours.toString().padStart(2, "0");
  const minuteString = minutes.toString().padStart(2, "0");
  const secondString = seconds.toString().padStart(2, "0");

  return {
    hours: [parseInt(hourString[0]), parseInt(hourString[1])],
    minutes: [parseInt(minuteString[0]), parseInt(minuteString[1])],
    seconds: [parseInt(secondString[0]), parseInt(secondString[1])],
    period: props.timeFormat === "12h" ? (hours >= 12 ? "PM" : "AM") : "",
  };
});

const getDateTimeZone = async () => {
  try {
    const formData = new FormData();
    formData.append("time_zone", props.timeZone);
    const response = await displayService.getDateTime(formData);
    const apiDateTime = response.dateTime; // "2025-07-09 15:14:39"
    
    // Parse the API date time
    const apiDate = moment(apiDateTime, 'YYYY-MM-DD HH:mm:ss');
    const localDate = moment(); // Current local time
    
    // Calculate the offset between API time and local time
    // This offset will be used to maintain the correct timezone time
    timezoneOffset.value = apiDate.valueOf() - localDate.valueOf();
    
    // Set the current time to the timezone time
    currentTime.value = apiDate.toDate();
    
    console.log('Clock - Timezone offset calculated:', timezoneOffset.value, 'ms');
    console.log('Clock - API time:', apiDateTime);
    console.log('Clock - Local time:', localDate.format('YYYY-MM-DD HH:mm:ss'));
    
  } catch (e) {
    console.log("error getting dateTimezone for clock", e);
    // Fallback to local time if API fails
    timezoneOffset.value = null;
    currentTime.value = new Date();
  }
};

function triggerFlipAnimation(digitType: string, position: number) {
  const element: any = document.querySelector(`.flip-${digitType}-${position}`);
  if (element) {
    element.classList.remove("play");
    void element.offsetWidth;
    element.classList.add("play");
  }
}

watch(
  digitalTime,
  (newTime, oldTime) => {
    if (oldTime) {
      if (newTime.hours[0] !== oldTime.hours[0]) {
        triggerFlipAnimation("hours", 0);
      }
      if (newTime.hours[1] !== oldTime.hours[1]) {
        triggerFlipAnimation("hours", 1);
      }
      if (newTime.minutes[0] !== oldTime.minutes[0]) {
        triggerFlipAnimation("minutes", 0);
      }
      if (newTime.minutes[1] !== oldTime.minutes[1]) {
        triggerFlipAnimation("minutes", 1);
      }
      if (props.showSeconds) {
        if (newTime.seconds[0] !== oldTime.seconds[0]) {
          triggerFlipAnimation("seconds", 0);
        }
        if (newTime.seconds[1] !== oldTime.seconds[1]) {
          triggerFlipAnimation("seconds", 1);
        }
      }
    }
  },
  { deep: true }
);

//ul style
const height = computed(() => props.config.height || 100);
const width = computed(() => props.config.width || 100);
const flipHeight = computed(() => Math.floor(height.value));
const flipWidth = computed(() => Math.floor(width.value * 0.23));
// const clockFontSize = computed(() => Math.floor(height.value * 0.95));
const clockFontSize = computed(() => {
  if (props.fontSize == 0 || !props.fontSize)
    return Math.floor(height.value * 0.75);
  return props.fontSize;
});

onMounted(async () => {
  // Only call API if timeZone is provided and not empty
  if (props.timeZone && props.timeZone.trim() !== '') {
    await getDateTimeZone();
  } else {
    // Use local time if no timezone specified or empty string
    timezoneOffset.value = null;
    currentTime.value = new Date();
  }
  
  previousTime.value = { ...digitalTime.value };

  const interval = props.showSeconds ? 1000 : 60000;
  clockTimer.value = window.setInterval(updateTime, interval);
});

onBeforeUnmount(() => {
  if (clockTimer.value) {
    window.clearInterval(clockTimer.value);
  }
});

function updateTime() {
  if (timezoneOffset.value !== null) {
    // Apply the timezone offset to maintain the correct timezone time
    const localTime = new Date();
    const timezoneTime = new Date(localTime.getTime() + timezoneOffset.value);
    currentTime.value = timezoneTime;
  } else {
    // Use local time if no timezone offset
    currentTime.value = new Date();
  }
}

function getDigitRange(digitType: string, position: number) {
  if (digitType === "hours" && position === 0) {
    return props.timeFormat === "12h" ? [0, 1] : [0, 1, 2];
  } else if (
    (digitType === "minutes" && position === 0) ||
    (digitType === "seconds" && position === 0)
  ) {
    return [0, 1, 2, 3, 4, 5];
  }
  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
}

function getPreviousDigit(current: number, max: number) {
  return current === 0 ? max - 1 : current - 1;
}

function hexToRgba(hex: string, alpha: number) {
  let c = hex.replace("#", "");
  if (c.length === 3)
    c = c
      .split("")
      .map((x) => x + x)
      .join("");
  const num = parseInt(c, 16);
  return `rgba(${(num >> 16) & 255}, ${(num >> 8) & 255}, ${
    num & 255
  }, ${alpha})`;
}

const boxShadowColor = computed(() => hexToRgba(props.backgroundColor, 0.7));
</script>

<script lang="ts">
export default {
  name: "ClckWidget",
};
</script>

<template>
  <template v-if="template == 'default'">
    <div
      class="clock-widget"
      :style="{
        fontFamily: fontFamily,
        fontSize: `${scaledFontSize}px`,
        color: fontColor,
        fontWeight: fontWeight === 'bold' ? 'bold' : 'normal',
        fontStyle: fontWeight === 'italic' ? 'italic' : 'normal',
        textDecoration: fontWeight === 'underline' ? 'underline' : 'none',
      }"
    >
      {{ formattedTime }}
    </div>
  </template>

  <template v-if="template == 'digital'">
    <div class="digital-clock-container">
      <div
        class="clock-inner"
        :style="{
          maxWidth: '100%',
          maxHeight: '100%',
          width: '100%',
          height: '100%',
          aspectRatio: (props.showSeconds ? 6 : 4) + ' / 1',
          margin: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }"
      >
        <!-- hours -->
        <div class="time-section" style="height: 100%">
          <!-- first hour digit -->
          <ul
            class="flip flip-hours-0"
            :style="{
              height: '100%',
              aspectRatio: '2/3',
              fontSize: clockFontSize + 'px',
              boxShadow: `0 2px 5px ${boxShadowColor}`,
            }"
          >
            <li
              v-for="digit in getDigitRange('hours', 0)"
              :key="`h0-${digit}`"
              :class="{
                active: digit === digitalTime.hours[0],
                before:
                  digit ===
                  getPreviousDigit(
                    digitalTime.hours[0],
                    props.timeFormat === '12h' ? 2 : 3
                  ),
              }"
            >
              <a href="#">
                <div class="up">
                  <div class="shadow"></div>
                  <div class="inn">{{ digit }}</div>
                </div>
                <div class="down">
                  <div class="shadow"></div>
                  <div class="inn">{{ digit }}</div>
                </div>
              </a>
            </li>
          </ul>
          <!-- second hour digit -->
          <ul
            class="flip flip-hours-1"
            :style="{
              height: '100%',
              aspectRatio: '2/3',
              fontSize: clockFontSize + 'px',
              boxShadow: `0 2px 5px ${boxShadowColor}`,
            }"
          >
            <li
              v-for="digit in getDigitRange('hours', 1)"
              :key="`h1-${digit}`"
              :class="{
                active: digit === digitalTime.hours[1],
                before: digit === getPreviousDigit(digitalTime.hours[1], 10),
              }"
            >
              <a href="#">
                <div class="up">
                  <div class="shadow"></div>
                  <div class="inn">{{ digit }}</div>
                </div>
                <div class="down">
                  <div class="shadow"></div>
                  <div class="inn">{{ digit }}</div>
                </div>
              </a>
            </li>
          </ul>
        </div>
        <!-- separator -->
        <div class="dots-separator">
          <div class="dot" v-if="showSeperator"></div>
          <div class="dot" v-if="showSeperator"></div>
        </div>
        <!-- minutes -->
        <div class="time-section" style="height: 100%">
          <!-- first minute digit -->
          <ul
            class="flip flip-minutes-0"
            :style="{
              height: '100%',
              aspectRatio: '2/3',
              fontSize: clockFontSize + 'px',
              boxShadow: `0 2px 5px ${boxShadowColor}`,
            }"
          >
            <li
              v-for="digit in getDigitRange('minutes', 0)"
              :key="`m0-${digit}`"
              :class="{
                active: digit === digitalTime.minutes[0],
                before: digit === getPreviousDigit(digitalTime.minutes[0], 6),
              }"
            >
              <a href="#">
                <div class="up">
                  <div class="shadow"></div>
                  <div class="inn">{{ digit }}</div>
                </div>
                <div class="down">
                  <div class="shadow"></div>
                  <div class="inn">{{ digit }}</div>
                </div>
              </a>
            </li>
          </ul>
          <!-- second minute digit -->
          <ul
            class="flip flip-minutes-1"
            :style="{
              height: '100%',
              aspectRatio: '2/3',
              fontSize: clockFontSize + 'px',
              boxShadow: `0 2px 5px ${boxShadowColor}`,
            }"
          >
            <li
              v-for="digit in getDigitRange('minutes', 1)"
              :key="`m1-${digit}`"
              :class="{
                active: digit === digitalTime.minutes[1],
                before: digit === getPreviousDigit(digitalTime.minutes[1], 10),
              }"
            >
              <a href="#">
                <div class="up">
                  <div class="shadow"></div>
                  <div class="inn">{{ digit }}</div>
                </div>
                <div class="down">
                  <div class="shadow"></div>
                  <div class="inn">{{ digit }}</div>
                </div>
              </a>
            </li>
          </ul>
        </div>
        <!-- separator (if seconds enabled) -->
        <div v-if="showSeconds" class="dots-separator">
          <div class="dot" v-if="showSeperator"></div>
          <div class="dot" v-if="showSeperator"></div>
        </div>
        <!-- seconds -->
        <template v-if="showSeconds">
          <div class="time-section" style="height: 100%">
            <!-- first second digit -->
            <ul
              class="flip flip-seconds-0"
              :style="{
                height: '100%',
                aspectRatio: '2/3',
                fontSize: clockFontSize + 'px',
                boxShadow: `0 2px 5px ${boxShadowColor}`,
              }"
            >
              <li
                v-for="digit in getDigitRange('seconds', 0)"
                :key="`s0-${digit}`"
                :class="{
                  active: digit === digitalTime.seconds[0],
                  before: digit === getPreviousDigit(digitalTime.seconds[0], 6),
                }"
              >
                <a href="#">
                  <div class="up">
                    <div class="shadow"></div>
                    <div class="inn">{{ digit }}</div>
                  </div>
                  <div class="down">
                    <div class="shadow"></div>
                    <div class="inn">{{ digit }}</div>
                  </div>
                </a>
              </li>
            </ul>
            <!-- second second digit -->
            <ul
              class="flip flip-seconds-1"
              :style="{
                height: '100%',
                aspectRatio: '2/3',
                fontSize: clockFontSize + 'px',
                boxShadow: `0 2px 5px ${boxShadowColor}`,
              }"
            >
              <li
                v-for="digit in getDigitRange('seconds', 1)"
                :key="`s1-${digit}`"
                :class="{
                  active: digit === digitalTime.seconds[1],
                  before:
                    digit === getPreviousDigit(digitalTime.seconds[1], 10),
                }"
              >
                <a href="#">
                  <div class="up">
                    <div class="shadow"></div>
                    <div class="inn">{{ digit }}</div>
                  </div>
                  <div class="down">
                    <div class="shadow"></div>
                    <div class="inn">{{ digit }}</div>
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </template>
        <!-- am pm -->
        <div v-if="timeFormat === '12h'" class="period">
          {{ digitalTime.period }}
        </div>
      </div>
    </div>
  </template>
</template>

<style scoped>
.clock-widget {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
  padding: 10px;
}

/* Digital Clock Styles */
.digital-clock-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  overflow: hidden;
}

.clock-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
  margin: auto;
}

.time-section {
  display: flex;
  gap: 5px;
  position: relative;
  height: 100%;
  min-height: 0;
}

/* separator */
.dots-separator {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin: 0 8px;
}

.dot {
  width: 8px;
  height: 8px;
  background-color: v-bind(seperatorColor);
  box-shadow: 0 1px 2px v-bind(seperatorColor);
  border-radius: 50%;
}

/* Indicator Dots */
.indicator-dot {
  width: 12px;
  height: 12px;
  background-color: transparent;
  border-radius: 50%;
  position: absolute;
  z-index: 10;
}

.left-dot {
  left: -25px;
  top: 50%;
  transform: translateY(-50%);
}

.top-dot {
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
}

.separator {
  font-size: 60px;
  font-weight: bold;
  color: #ccc;
  text-shadow: 0 1px 2px #000;
  line-height: 90px;
  margin: 0 5px;
}

.period {
  font-size: 24px;
  font-weight: bold;
  color: v-bind(seperatorColor);
  text-shadow: 0 1px 2px #000;
  margin-left: 10px;
  align-self: flex-start;
  margin-top: 10px;
}

/* Flip Clock Styles */
ul.flip {
  position: relative;
  float: left;
  /* margin: 2px; */
  height: 100%;
  /* aspect-ratio: 2/3; */
  font-size: inherit;
  font-weight: bold;
  border-radius: 6px;
  box-shadow: 0px 2px 5px v-bind(boxShadowColor);
  list-style: none;
  padding: 0;
  border:none;
}

ul.flip li {
  z-index: 1;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  border:none;
}

ul.flip li.active {
  z-index: 2;
}

ul.flip li a {
  display: block;
  height: 100%;
  perspective: 200px;
  text-decoration: none;
  border: none;
}

ul.flip li a div {
  z-index: 1;
  position: absolute;
  left: 0;
  width: 100%;
  height: 50%;
  overflow: hidden;
}

ul.flip li a div .shadow {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 2;
}

ul.flip li a div.up {
  transform-origin: 50% 100%;
  top: 0;
  border: none;
  backface-visibility: hidden;
  transform-style: preserve-3d;
}

/* ul.flip li a div.up:after {
  content: "";
  position: absolute;
  top: 36px;
  left: 0;
  z-index: 5;
  width: 100%;
  height: 2px;
  background-color: rgba(0, 0, 0, 0.4);
} */

ul.flip li a div.down {
  transform-origin: 50% 0%;
  bottom: 0;
  backface-visibility: hidden;
  transform-style: preserve-3d;
}

ul.flip li a div div.inn {
  position: absolute;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 200%;
  line-height: 200%;
  color: v-bind(fontColor);
  text-shadow: 0 1px 2px #000;
  text-align: center;
  background-color: v-bind(backgroundColor);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

ul.flip li a div.up div.inn {
  top: 0;
}

ul.flip li a div.down div.inn {
  bottom: 0;
}

/* animation */
ul.flip.play li.before {
  z-index: 3;
}

ul.flip.play li.active {
  animation: flipActive 0.5s 0.5s linear both;
  z-index: 2;
}

@keyframes flipActive {
  0% {
    z-index: 2;
  }
  5% {
    z-index: 4;
  }
  100% {
    z-index: 4;
  }
}

ul.flip.play li.active .down {
  z-index: 2;
  animation: turnDown 0.5s 0.5s linear both;
}

@keyframes turnDown {
  0% {
    transform: rotateX(90deg);
  }
  100% {
    transform: rotateX(0deg);
  }
}

ul.flip.play li.before .up {
  z-index: 2;
  animation: turnUp 0.5s linear both;
}

@keyframes turnUp {
  0% {
    transform: rotateX(0deg);
  }
  100% {
    transform: rotateX(-90deg);
  }
}

/* Shadow Effects */
ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

li {
  list-style: none;
  padding: 0;
  margin: 0;
}

ul.flip.play li.before .up .shadow {
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 1) 100%
  );
  animation: showShadow 0.5s linear both;
}

ul.flip.play li.active .up .shadow {
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 1) 100%
  );
  animation: hideShadow 0.5s 0.3s linear both;
}

ul.flip.play li.before .down .shadow {
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 0.1) 100%
  );
  animation: showShadow 0.5s linear both;
}

ul.flip.play li.active .down .shadow {
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 0.1) 100%
  );
  animation: hideShadow 0.5s 0.3s linear both;
}

@keyframes showShadow {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes hideShadow {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
</style>
